import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import { LanguageProvider } from './context/LanguageContext';
import { NotificationsProvider } from './context/NotificationsContext';
import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import Assessment from './pages/Assessment';
import Simulations from './pages/Simulations';
import PhishingManual from './pages/PhishingManual';
import SocialEngineeringChat from './pages/SocialEngineeringChat';
import SimulationPhishingIntro from './pages/SimulationPhishingIntro';
import SimulationSocialEngineeringIntro from './pages/SimulationSocialEngineeringIntro';
import BruteForceLab from './pages/BruteForceLab';
import PhishingTestResult from './pages/PhishingTestResult';
import History from './pages/History';
import Report from './pages/Report';
import Organization from './pages/Organization';
import CreateOrganization from './pages/CreateOrganization';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0ea5e9]" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Home />
          </PrivateRoute>
        }
      />
      <Route
        path="/organizations/new"
        element={
          <PrivateRoute>
            <CreateOrganization />
          </PrivateRoute>
        }
      />
      <Route
        path="/org/:organizationId/dashboard"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/org/:organizationId/assessment"
        element={
          <PrivateRoute>
            <Assessment />
          </PrivateRoute>
        }
      />
      <Route
        path="/org/:organizationId/simulations"
        element={
          <PrivateRoute>
            <Simulations />
          </PrivateRoute>
        }
      />
      <Route
        path="/org/:organizationId/simulations/phishing-intro"
        element={
          <PrivateRoute>
            <SimulationPhishingIntro />
          </PrivateRoute>
        }
      />
      <Route
        path="/org/:organizationId/simulations/social-engineering-intro"
        element={
          <PrivateRoute>
            <SimulationSocialEngineeringIntro />
          </PrivateRoute>
        }
      />
      <Route
        path="/org/:organizationId/simulations/brute-force-lab"
        element={
          <PrivateRoute>
            <BruteForceLab />
          </PrivateRoute>
        }
      />
      <Route
        path="/org/:organizationId/phishing-manual"
        element={
          <PrivateRoute>
            <PhishingManual />
          </PrivateRoute>
        }
      />
      <Route
        path="/org/:organizationId/social-engineering-chat"
        element={
          <PrivateRoute>
            <SocialEngineeringChat />
          </PrivateRoute>
        }
      />
      <Route path="/phishing-test/:token" element={<PhishingTestResult />} />
      <Route
        path="/org/:organizationId/history"
        element={
          <PrivateRoute>
            <History />
          </PrivateRoute>
        }
      />
      <Route
        path="/org/:organizationId/report/:assessmentId"
        element={
          <PrivateRoute>
            <Report />
          </PrivateRoute>
        }
      />
      <Route
        path="/org/:organizationId/organization"
        element={
          <PrivateRoute>
            <Organization />
          </PrivateRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SettingsProvider>
          <LanguageProvider>
            <NotificationsProvider>
              <AppRoutes />
            </NotificationsProvider>
          </LanguageProvider>
        </SettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
